/**
 * Browser Push helpers.
 * Subscribes via PushManager (using a VAPID key fetched from /api/push/vapid-key)
 * and stores the subscription in the backend so the server can fan out pushes.
 */
import axios from "axios";

const urlBase64ToUint8Array = (b64) => {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const safe = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(safe);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

export function isPushSupported() {
  if (typeof window === "undefined") return false;
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function pushPermission() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission; // "granted" | "denied" | "default"
}

async function getReg() {
  return (
    (await navigator.serviceWorker.getRegistration()) ||
    (await navigator.serviceWorker.ready)
  );
}

export async function getActiveSubscription() {
  if (!isPushSupported()) return null;
  const reg = await getReg();
  return reg ? reg.pushManager.getSubscription() : null;
}

export async function subscribePush(api) {
  if (!isPushSupported()) throw new Error("Push is not supported on this browser");
  if (Notification.permission === "denied") {
    throw new Error("Notifications are blocked. Allow them in your browser settings to enable.");
  }
  if (Notification.permission !== "granted") {
    const result = await Notification.requestPermission();
    if (result !== "granted") throw new Error("Notifications permission denied");
  }
  const { data } = await axios.get(`${api}/push/vapid-key`);
  const reg = await getReg();
  if (!reg) throw new Error("Service worker not ready");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });
  }
  const payload = sub.toJSON();
  await axios.post(`${api}/push/subscribe`, {
    endpoint: payload.endpoint,
    keys: { p256dh: payload.keys.p256dh, auth: payload.keys.auth },
  });
  return sub;
}

export async function unsubscribePush(api) {
  const sub = await getActiveSubscription();
  if (!sub) return;
  try {
    await axios.post(`${api}/push/unsubscribe`, { endpoint: sub.endpoint });
  } catch (e) {
    // continue even if backend call fails — we still want to unsubscribe locally
  }
  await sub.unsubscribe();
}

export async function sendTestPush(api, payload = {}) {
  await axios.post(`${api}/push/test`, payload);
}
